import { FieldType } from '@databases/model/field';
import { type MutableValue, UserType } from '@databases/model/value';
import { DatabaseFieldType } from '@databases/types/database';

import { MultiValueChangeType, type ValueChange } from '../../../database/changesets';

export function applyDeltaValue(
  getMutableValue: <T extends FieldType>(type: T) => MutableValue<T>,
  change: ValueChange
) {
  switch (change.type) {
    case DatabaseFieldType.TEXT: {
      const textValue = getMutableValue(FieldType.Text);
      if ('adf' in change) {
        textValue.setContent(change.adf);
      } else {
        textValue.applyDelta(change.delta);
      }
      break;
    }
    case DatabaseFieldType.SELECT: {
      const value = getMutableValue(FieldType.Select);
      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add(change.delta.value);
            break;
          case MultiValueChangeType.EDIT:
            throw new Error('unsupported');
          case MultiValueChangeType.REMOVE:
            value.delete([value.value[change.delta.index]]);
            break;
          case MultiValueChangeType.REORDER:
            throw new Error('unsupported');
          case MultiValueChangeType.SET_TO:
            value.setTo(change.delta.value);
        }
      }
      break;
    }
    case DatabaseFieldType.ATTACHMENT: {
      const value = getMutableValue(FieldType.Attachment);
      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              fileId:
                'fileId' in change.delta.value ? change.delta.value.fileId || '' : '',
              id: change.delta.value._id,
            });

            break;
          case MultiValueChangeType.EDIT:
            if (value.value[change.delta.index]) {
              if ('fileId' in change.delta.value) {
                value.value[change.delta.index].fileId = change.delta.value.fileId;
              }
            } else {
              if ('fileId' in change.delta.value) {
                value.add({
                  fileId: change.delta.value.fileId,
                  id: change.delta.value._id,
                });
              }
            }

            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => {
                return {
                  id: v._id,
                  fileId: 'fileId' in v ? v.fileId || '' : '',
                };
              })
            );
        }
      }
      break;
    }
    case DatabaseFieldType.USER: {
      const value = getMutableValue(FieldType.User);
      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND: {
            const deltaValue = change.delta.value;
            value.add(
              typeof deltaValue === 'string'
                ? {
                    type: UserType.Current,
                  }
                : {
                    id: deltaValue._id,
                    ...('accountId' in deltaValue && { accountId: deltaValue.accountId }),
                    type: UserType.Referenced,
                  }
            );
            break;
          }
          case MultiValueChangeType.EDIT: {
            const deltaValue = change.delta.value;
            const user = value.value[change.delta.index];
            if (typeof deltaValue !== 'string') {
              if (user.type === UserType.Referenced) {
                user.accountId =
                  'accountId' in deltaValue ? deltaValue.accountId || '' : '';
              } else {
                // need to change type
                value.delete(change.delta.index);
                value.insert(change.delta.index, {
                  id: deltaValue._id,
                  ...('accountId' in deltaValue && { accountId: deltaValue.accountId }),
                  type: UserType.Referenced,
                });
              }
            } else {
              if (user.type === UserType.Referenced) {
                // need to change type
                value.delete(change.delta.index);
                value.insert(change.delta.index, {
                  type: UserType.Current,
                });
              }
            }
            break;
          }
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            if (typeof change.delta.value !== 'string') {
              value.setTo(
                change.delta.value.map((v) => ({
                  id: v._id,
                  ...('accountId' in v && { accountId: v.accountId }),
                  type: UserType.Referenced,
                }))
              );
            } else {
              value.setTo([{ type: UserType.Current }]);
            }
        }
      }
      break;
    }
    case DatabaseFieldType.CHECKBOX: {
      getMutableValue(FieldType.Checkbox).value = change.value;
      break;
    }
    case DatabaseFieldType.PAGE_LINK: {
      const value = getMutableValue(FieldType.PageLink);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              contentId:
                'contentId' in change.delta.value
                  ? change.delta.value.contentId
                  : undefined,
              id: change.delta.value._id,
              ari: 'ari' in change.delta.value ? change.delta.value.ari : undefined,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].contentId =
              'contentId' in change.delta.value
                ? change.delta.value.contentId
                : undefined;
            value.value[change.delta.index].ari =
              'ari' in change.delta.value ? change.delta.value.ari : undefined;
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => ({
                id: v._id,
                contentId: 'contentId' in v ? v.contentId || undefined : undefined,
                ari: 'ari' in v ? v.ari || undefined : undefined,
              }))
            );
        }
      }
      break;
    }
    case DatabaseFieldType.ENTRY_LINK: {
      const value = getMutableValue(FieldType.EntryLink);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              entryId:
                'entryId' in change.delta.value ? change.delta.value.entryId || '' : '',
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].entryId =
              'entryId' in change.delta.value ? change.delta.value.entryId || '' : '';
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => {
                return {
                  id: v._id,
                  entryId: 'entryId' in v ? v.entryId || '' : '',
                };
              })
            );
        }
      }
      break;
    }
    case DatabaseFieldType.HYPERLINK: {
      const value = getMutableValue(FieldType.Hyperlink);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              url: change.delta.value.url,
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].applyUrlDelta(change.delta.delta);
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => ({
                url: v.url,
                id: v._id,
              }))
            );
        }
      }
      break;
    }
    case DatabaseFieldType.JIRA_ISSUE: {
      const value = getMutableValue(FieldType.JiraIssue);

      if (value) {
        switch (change.delta.type) {
          case MultiValueChangeType.APPEND:
            value.add({
              key: 'key' in change.delta.value ? change.delta.value.key : '',
              id: change.delta.value._id,
            });
            break;
          case MultiValueChangeType.EDIT:
            value.value[change.delta.index].key =
              'key' in change.delta.value ? change.delta.value.key : '';
            break;
          case MultiValueChangeType.REMOVE:
            value.delete(change.delta.index);
            break;
          case MultiValueChangeType.REORDER:
            value.reorder(change.delta.currentIndex, change.delta.newIndex);
            break;
          case MultiValueChangeType.SET_TO:
            value.setTo(
              change.delta.value.map((v) => ({
                key: 'key' in v ? v.key : '',
                id: v._id,
              }))
            );
        }
      }
      break;
    }
    case DatabaseFieldType.NUMBER: {
      getMutableValue(FieldType.Number).value = change.value;
      break;
    }
    case DatabaseFieldType.DATE: {
      getMutableValue(FieldType.Date).value = change.value || ''; // FIXME: type can be undefined?
      break;
    }
  }
}
